import { Card, CardHeader, CardTitle, CardBody, Chip } from '@vnetwork/ui/primitives';
import { InfoGrid, type InfoItem } from '@/pages/account/components/InfoGrid';
import { formatDate, formatMoney } from '@/lib/format';
import { TableLink } from '@/components/table-link';
import { EntityLink } from '@/components/entity-link';
import {
  useServiceDates,
  useServiceContract,
  type ServiceDetail,
} from '@/api/services';
import { ServiceConfigTable } from './service-config-table';
import { ServiceActivityLog } from './service-activity-log';
import { ServicePriceChangeAlert } from './service-price-change-alert';
import { ServiceMetadataCard } from './service-metadata-card';
import { ServiceActivationWorkflow } from './activation-workflow/activation-workflow-section';

const muted = { color: 'var(--vn-fg-muted)' } as const;

/** A labelled range "start → end", or an em-dash placeholder. */
function rangeValue(start?: string | null, end?: string | null) {
  if (!start && !end) return <span style={muted}>—</span>;
  return `${formatDate(start)} → ${formatDate(end)}`;
}

/** Link to the owning customer: `{id} – {company_name | customer_name}` on a single line. */
function customerValue(detail: ServiceDetail) {
  if (!detail.customer_id) return detail.company_name || detail.customer_name || undefined;
  return (
    <EntityLink
      to={`/customers/${detail.customer_id}`}
      id={detail.customer_id}
      name={detail.company_name || detail.customer_name}
    />
  );
}

/**
 * Generic (template-agnostic) service summary — the plain-language "what's the
 * deal" view for sales/CS. Four fact cards: Identity, Customer & contract,
 * Lifecycle (from `/dates`), and Pricing & people. The per-type config editor,
 * internal-ticket workflow and lifecycle actions are S3.
 */
export function ServiceInformationTab({ detail }: { detail: ServiceDetail }) {
  const id = detail.service_id;
  const { data: datesData } = useServiceDates(id);
  const { data: contractData } = useServiceContract(id);
  const dates = datesData?.result;
  const contract = contractData?.results?.[0];

  const identity: InfoItem[] = [
    { label: 'Service ID', value: detail.service_id },
    { label: 'Service name', value: detail.service_name },
    { label: 'Entity name', value: detail.name || <span style={muted}>—</span> },
    { label: 'Template', value: detail.service_template },
    { label: 'Product group', value: detail.product_group_name },
    { label: 'Type', value: detail.service_type || <span style={muted}>—</span> },
    { label: 'POC', value: <Chip appearance="subtle" tone={detail.is_poc ? 'warning' : 'neutral'} size="sm">{detail.is_poc ? 'Yes' : 'No'}</Chip> },
    { label: 'Taxable', value: <Chip appearance="subtle" tone={detail.service_taxable ? 'success' : 'neutral'} size="sm">{detail.service_taxable ? 'Yes' : 'No'}</Chip> },
  ];

  const relationships: InfoItem[] = [
    { label: 'Customer', value: customerValue(detail) },
    {
      label: 'Contract',
      value: contract ? (
        <TableLink to={`/contracts/${contract.contract_id}`}>
          {contract.contract_number_id || `#${contract.contract_id}`}
        </TableLink>
      ) : detail.contract_id ? (
        <TableLink to={`/contracts/${detail.contract_id}`}>
          {`#${detail.contract_id}`}
        </TableLink>
      ) : (
        <span style={muted}>—</span>
      ),
    },
    { label: 'Role', value: detail.contract_role || <span style={muted}>—</span> },
    { label: 'Direction', value: detail.contract_direction || <span style={muted}>—</span> },
  ];

  const lifecycle: InfoItem[] = [
    { label: 'Start date', value: formatDate(detail.service_start_date) },
    { label: 'Stopped date', value: detail.service_stopped_date ? formatDate(detail.service_stopped_date) : <span style={muted}>—</span> },
    { label: 'First cycle', value: rangeValue(dates?.first_startdate, dates?.first_enddate) },
    { label: 'Current cycle', value: rangeValue(dates?.current_startdate, dates?.current_enddate) },
    { label: 'Renew cycle', value: rangeValue(dates?.continue_startdate, dates?.continue_enddate) },
    { label: 'Statement cycle', value: rangeValue(dates?.statement_startdate, dates?.statement_enddate) },
  ];

  const currency = detail.service_currency || 'VND';

  const commercials: InfoItem[] = [
    { label: 'Register price', value: formatMoney(detail.service_price_register, currency) },
    { label: 'Recurring price', value: formatMoney(detail.service_price_recurring, currency) },
    { label: 'Currency', value: detail.service_currency || 'VND' },
    { label: 'Exchange rate', value: detail.service_exchange_rate ?? 1 },
    { label: 'Tax %', value: detail.service_tax_percentage != null ? `${(detail.service_tax_percentage * 100).toFixed(0)}%` : <span style={muted}>—</span> },
    { label: 'Pricing model', value: detail.service_pricing_model || <span style={muted}>—</span> },
    { label: 'CS followers', value: detail.cs_followers?.filter(Boolean).join(', ') || <span style={muted}>—</span> },
    { label: 'Sale tracking', value: detail.employee_names?.filter(Boolean).join(', ') || <span style={muted}>—</span> },
  ];

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      {/* Internal-ticket confirm/activation workflow (CS verify → provision → done). */}
      <ServiceActivationWorkflow detail={detail} />
      <ServicePriceChangeAlert id={id} currency={currency} />
      {/* Per-template metadata (Domain registrant, Other.Standard schedule). */}
      <ServiceMetadataCard detail={detail} />
      {/* The provisioned configuration is the primary view (mirrors legacy).
          Domains carry no configoptions (their config is the metadata above). */}
      {detail.service_template.startsWith('Domain.') ? null : <ServiceConfigTable detail={detail} />}
      <FactCard title="Identity" items={identity} />
      <FactCard title="Customer & contract" items={relationships} />
      <FactCard title="Lifecycle" items={lifecycle} />
      <FactCard title="Pricing & people" items={commercials} />
      <ServiceActivityLog id={id} />
    </div>
  );
}

function FactCard({ title, items }: { title: string; items: InfoItem[] }) {
  return (
    <Card flat>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardBody>
        <InfoGrid items={items} />
      </CardBody>
    </Card>
  );
}
